import type { HTMLAttributes, ReactNode } from 'react';

interface ListPageLayoutProps extends HTMLAttributes<HTMLElement> {
  children: ReactNode;
}

interface ListPageBodyProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/**
 * Vertical page layout for list-and-discover surfaces (Beat UI).
 *
 * Pairs a header (typically `<SectionPageHeader />`) above an optional
 * scroll-bounded body container (`<ListPageBody />`). Generic enough to
 * host either a grid of cards (Knowledge Hub V2) or a paginated table
 * (Projects).
 *
 * Composition pattern follows the monks-ui `Card` / `CardHeader` /
 * `CardContent` named-exports convention rather than React `Component.X`
 * compound members — this matches the rest of the design system surface
 * and is what every existing consumer file in the project expects.
 *
 * Dark-mode aware via design tokens (`bg-card`, `text-foreground`,
 * `text-foreground-alt`); no MUI, no inline styles.
 *
 * Usage:
 *   <ListPageLayout>
 *     <SectionPageHeader variant="compact" title="Foo" ... />
 *     <ListPageBody>
 *       {/* grid OR table — consumer decides *\/}
 *     </ListPageBody>
 *   </ListPageLayout>
 */
export function ListPageLayout({ children, className, ...rest }: ListPageLayoutProps) {
  const baseClasses = 'flex min-h-0 flex-1 flex-col gap-app-inset';
  const composedClassName = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <section data-testid="list-page-layout" className={composedClassName} {...rest}>
      {children}
    </section>
  );
}

/**
 * Body container for `ListPageLayout`. Provides the scrollable surface
 * that holds the list/grid content. Consumers nest a toolbar + table OR
 * a card grid here.
 *
 * Renders an opaque `bg-card` rounded surface — the canonical look used
 * by every list-page surface in the app (Projects table, Knowledge Hub
 * card grid, BriefTemplates list). Cards rendered INSIDE the body must
 * not carry their own `bg-card`; they inherit this surface via
 * `bg-transparent` (see `KnowledgeHubCard.tsx`).
 *
 * Tailwind-only; design tokens carry dark mode automatically.
 */
export function ListPageBody({ children, className, ...rest }: ListPageBodyProps) {
  const baseClasses =
    'flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-card text-foreground';
  const composedClassName = className ? `${baseClasses} ${className}` : baseClasses;

  return (
    <div data-testid="list-page-body" className={composedClassName} {...rest}>
      {children}
    </div>
  );
}
